import type { MeResponse } from '@pf/api-types';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  RouterProvider,
} from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import type { z } from 'zod';
import { ApiError, api } from './api.ts';
import { Shell } from './app/Shell.tsx';
import s from './app/Status.module.css';
import { applyTheme, savedTheme } from './app/theme.ts';
import { cx } from './kit/cx.ts';
import t from './kit/type.module.css';
import { useLive } from './live.ts';
import { Bookings } from './routes/Bookings.tsx';
import { Budget } from './routes/Budget.tsx';
import { Export } from './routes/Export.tsx';
import { Info } from './routes/Info.tsx';
import { Journal } from './routes/Journal.tsx';
import { Kit } from './routes/Kit.tsx';
import { MapKit } from './routes/MapKit.tsx';
import { Planner } from './routes/Planner.tsx';
import { Print } from './routes/Print.tsx';
import { SignIn } from './routes/SignIn.tsx';
import { Timeline } from './routes/Timeline.tsx';
import { Today } from './routes/Today.tsx';
import { Trips } from './routes/Trips.tsx';

type Me = z.infer<typeof MeResponse>;

function Session() {
  const queries = useQueryClient();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api<Me>('/me'),
    retry: (count, error) => !(error instanceof ApiError && error.status === 401) && count < 2,
    // Signed out, a focus refetch would swap the sign-in form for "Loading…" and lose its step
    // (coming back from the mail app with the code). Only re-check a session we have.
    refetchOnWindowFocus: (query) => query.state.data !== undefined,
  });
  const signedIn = me.isSuccess;
  // The dev simulator's demo account has no server to listen to.
  useLive(signedIn && !(import.meta.env.DEV && me.data?.user.email.endsWith('@pathfinder.invalid')));

  if (me.isPending)
    return (
      <p className={cx(t.readoutS, s.status)} aria-live="polite">
        Loading…
      </p>
    );
  if (me.error instanceof ApiError && me.error.status === 401) return <SignIn />;
  if (me.isError)
    return (
      <p role="alert" className={cx(t.bodyS, s.status, s.error)}>
        {me.error.message}
      </p>
    );

  const signOut = async () => {
    await api('/auth/signout', { method: 'POST', body: { scope: 'device' } }).catch(() => undefined);
    queries.clear();
    await queries.invalidateQueries({ queryKey: ['me'] });
  };

  return <Shell email={me.data.user.email} onSignOut={signOut} />;
}

// The live socket refetches what changed within seconds (and on reconnect), so data stays fresh for
// a minute: switching pages or windows doesn't refetch the trip each time.
const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 60_000 } } });

const root = createRootRoute({ component: Outlet });
// Everything behind sign-in lives under the Shell layout; /kit (the design kit) doesn't need a session.
const app = createRoute({ getParentRoute: () => root, id: 'app', component: Session });
const trip = createRoute({
  getParentRoute: () => app,
  path: '/trips/$tripId',
  validateSearch: (search: Record<string, unknown>): { day?: string } =>
    typeof search.day === 'string' ? { day: search.day } : {},
  component: Planner,
});
const router = createRouter({
  routeTree: root.addChildren([
    app.addChildren([
      createRoute({ getParentRoute: () => app, path: '/', component: Trips }),
      trip,
      createRoute({ getParentRoute: () => app, path: '/trips/$tripId/bookings', component: Bookings }),
      createRoute({ getParentRoute: () => app, path: '/trips/$tripId/budget', component: Budget }),
      createRoute({ getParentRoute: () => app, path: '/trips/$tripId/timeline', component: Timeline }),
      createRoute({ getParentRoute: () => app, path: '/trips/$tripId/info', component: Info }),
      createRoute({ getParentRoute: () => app, path: '/trips/$tripId/journal', component: Journal }),
      createRoute({ getParentRoute: () => app, path: '/trips/$tripId/export', component: Export }),
      createRoute({ getParentRoute: () => app, path: '/trips/$tripId/print', component: Print }),
    ]),
    createRoute({ getParentRoute: () => root, path: '/kit', component: Kit }),
    createRoute({ getParentRoute: () => root, path: '/kit/today', component: Today }),
    createRoute({ getParentRoute: () => root, path: '/kit/map', component: MapKit }),
    // The trip simulator (IMPL-CONNECT S7): dev builds only, so its scenarios never ship.
    ...(import.meta.env.DEV
      ? [
          createRoute({
            getParentRoute: () => root,
            path: '/dev',
            component: lazyRouteComponent(() => import('./dev/DevSimulator.tsx'), 'DevSimulator'),
          }),
        ]
      : []),
  ]),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

applyTheme(savedTheme()); // before the first paint, so a forced theme doesn't flash

const element = document.getElementById('root');
if (element) {
  createRoot(element).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}
